import type { CandlestickData, UTCTimestamp, HistogramData, SingleValueData } from 'lightweight-charts';
import { getUnixTime, getDateFromUnix } from '@einhaus/meteoric-helpers';
import type { BarOHLCV, BarOHLCVDetailed, IndicatorInput, Trendline } from '@einhaus/project314-types';
import { convertEstTimestampToTradingView } from './dateHelpers/dateHelpers.ts';
import { generateIndicatorKey } from '@einhaus/project314-shared';

export const filterEodRecordsByDate = (minDateTime: string, maxDateTime: string, bars: BarOHLCV[]): BarOHLCV[] => {
    const minTimestamp = getUnixTime(minDateTime);
    const maxTimestamp = getUnixTime(maxDateTime);
    return bars.filter((record) => record.t >= minTimestamp && record.t <= maxTimestamp);
};

export const convertEodRecordToTradingView = (eod: BarOHLCV[]): CandlestickData[] => {
    const newEod: CandlestickData[] = [];

    for (const bar of eod) {
        newEod.push({
            open: bar.o,
            close: bar.c,
            high: bar.h,
            low: bar.l,
            time: getDateFromUnix(bar.t)
        });
    }

    return newEod;
};

export const convertBarsToTradingView = (bars: BarOHLCVDetailed[]): CandlestickData[] => {
    const newBars: CandlestickData[] = [];

    // Any duplicate timestamps and lightweight-charts will throw an error
    const timestamps: Set<UTCTimestamp> = new Set();

    for (const bar of bars) {
        const time = convertEstTimestampToTradingView(bar.t);

        if (timestamps.has(time)) continue;

        timestamps.add(time);

        newBars.push({
            open: bar.o,
            close: bar.c,
            high: bar.h,
            low: bar.l,
            time
        });
    }

    return newBars;
};

export const convertDurationIndicatorBarsToTradingView = (config: {
    bars: BarOHLCVDetailed[];
    indicator: IndicatorInput;
    property?: string | undefined;
}): { time: UTCTimestamp; value: number }[] => {
    const { bars, indicator, property } = config;
    const data: { time: UTCTimestamp; value: number }[] = [];
    const indicatorString = generateIndicatorKey(indicator);

    for (const bar of bars) {
        const base = bar.i?.[indicatorString];
        if (base == null) continue; // skip if missing or null

        let value: number | undefined;

        if (!property) {
            // If no property is given, we expect `base` to be a direct number
            if (typeof base !== 'number') {
                // Not a number => skip
                continue;
            }

            value = base;
        } else if (typeof base !== 'object') {
            // If a property IS given, we expect `base` to be an object
            // If it's not an object, skip
            continue;
        } else {
            // Here, we treat base as an object of shape { [key: string]: number|undefined }
            // but MacdOutput or StochasticRsiOutput might be other shapes,
            // so we cast via `unknown` first:
            const obj = base as unknown as Record<string, number | undefined>;
            const maybeVal = obj[property];

            if (typeof maybeVal !== 'number') {
                // Not a valid number => skip
                continue;
            }

            value = maybeVal;
        }

        if (!value) continue;

        data.push({
            value,
            time: convertEstTimestampToTradingView(bar.t)
        });
    }

    return data;
};

export const createTradingViewVolumeFromEod = (bars: BarOHLCV[]): HistogramData[] => {
    const newEod: HistogramData[] = [];

    for (const bar of bars) {
        newEod.push({
            time: getDateFromUnix(bar.t),
            value: bar.v,
            color: bar.c > bar.o ? '#1D605F' : '#813539'
        });
    }

    return newEod;
};

export const createTrendlineDataFromBars = (trendline: Trendline): SingleValueData[] => {
    const data: SingleValueData[] = [];

    const { barInteractions } = trendline;

    const startingPrice = barInteractions[0]?.tp;

    if (!startingPrice) return data;

    for (const interaction of barInteractions) {
        data.push({
            time: convertEstTimestampToTradingView(interaction.t),
            value: interaction.tp
        });
    }

    return data;
};

export const createLevelLineDataFromBars = (bars: BarOHLCV[], level: number): SingleValueData[] => {
    const data: SingleValueData[] = [];

    for (const bar of bars) {
        data.push({
            time: convertEstTimestampToTradingView(bar.t),
            value: level
        });
    }

    return data;
};

export const createTradingViewVolumeFromBars = (bars: BarOHLCV[]): HistogramData[] => {
    const newEod: HistogramData[] = [];

    for (const bar of bars) {
        newEod.push({
            time: convertEstTimestampToTradingView(bar.t),
            value: bar.v,
            color: bar.c > bar.o ? '#1D605F' : '#813539'
        });
    }

    return newEod;
};

export const createTrendlineDataFromIntraday = (bars: BarOHLCV[], trendPercent: number): SingleValueData[] => {
    const data: SingleValueData[] = [];
    let lastTrendlinePrice = 0;
    let i = 0;

    for (const bar of bars) {
        if (trendPercent > 0) {
            lastTrendlinePrice = i === 0 ? bar.l : lastTrendlinePrice + lastTrendlinePrice * (trendPercent / 100);
        } else {
            lastTrendlinePrice = i === 0 ? bar.h : lastTrendlinePrice + lastTrendlinePrice * (trendPercent / 100);
        }

        data.push({
            time: getDateFromUnix(bar.t, 'ymdhms'),
            value: lastTrendlinePrice
        });

        i++;
    }

    return data;
};
